import { API } from "../api";
import {
  mapMarketplaceCategory,
  mapMarketplaceProductCard,
  mapMarketplaceProductDetail,
  mapMarketplaceOrder,
} from "../../utils/investorMarketplaceAdapter";

function transformListResponse(responseData, mapItem) {
  const data = responseData?.data;
  return {
    items: (data?.items || []).map(mapItem).filter(Boolean),
    pagination: data?.pagination || {},
  };
}

export const investorMarketplaceApi = API.injectEndpoints({
  endpoints: (builder) => ({
    listMarketplaceCategories: builder.query({
      query: (params = {}) => ({
        url: "/investors/marketplace/categories",
        method: "GET",
        params,
      }),
      transformResponse: (responseData) =>
        transformListResponse(responseData, mapMarketplaceCategory),
      providesTags: [{ type: "MarketplaceCategories", id: "LIST" }],
    }),
    listMarketplaceProducts: builder.query({
      query: (params = {}) => ({
        url: "/investors/marketplace/products",
        method: "GET",
        params,
      }),
      transformResponse: (responseData) =>
        transformListResponse(responseData, mapMarketplaceProductCard),
      providesTags: [{ type: "MarketplaceProducts", id: "LIST" }],
    }),
    getMarketplaceProduct: builder.query({
      query: (slug) => ({
        url: `/investors/marketplace/products/${encodeURIComponent(slug)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => mapMarketplaceProductDetail(responseData?.data),
      providesTags: (_r, _e, slug) => [{ type: "MarketplaceProduct", id: slug }],
    }),
    listMarketplaceOrders: builder.query({
      query: (params = {}) => ({
        url: "/investors/marketplace/orders",
        method: "GET",
        params,
      }),
      transformResponse: (responseData) =>
        transformListResponse(responseData, mapMarketplaceOrder),
      providesTags: [{ type: "MarketplaceOrders", id: "LIST" }],
    }),
    getMarketplaceOrder: builder.query({
      query: (orderNumber) => ({
        url: `/investors/marketplace/orders/${encodeURIComponent(orderNumber)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => mapMarketplaceOrder(responseData?.data),
      providesTags: (_r, _e, id) => [{ type: "MarketplaceOrder", id }],
    }),
    marketplaceOrderFeePreview: builder.mutation({
      query: (body) => ({
        url: "/investors/marketplace/orders/fee-preview",
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData?.data || {},
    }),
    createMarketplaceOrder: builder.mutation({
      query: (body) => ({
        url: "/investors/marketplace/orders",
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => {
        const data = responseData?.data || {};
        return {
          order: mapMarketplaceOrder(data),
          paymentUrl: data.payment_url ?? null,
          paymentReference: data.payment_reference ?? null,
          paymentProvider: data.payment_provider ?? null,
          alatpayCheckout: data.alatpay_checkout ?? null,
          onlinePaymentAvailable: data.online_payment_available === true,
          feeBreakdown: data.fee_breakdown ?? null,
          stockHoldExpiresAt: data.stock_hold_expires_at ?? null,
          message: data.message || responseData?.message || null,
        };
      },
      invalidatesTags: [
        { type: "MarketplaceOrders", id: "LIST" },
        { type: "MarketplaceProducts", id: "LIST" },
      ],
    }),
    cancelMarketplaceOrder: builder.mutation({
      query: ({ orderNumber, reason }) => ({
        url: `/investors/marketplace/orders/${encodeURIComponent(orderNumber)}/cancel`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      transformResponse: (responseData) => mapMarketplaceOrder(responseData?.data),
      invalidatesTags: (_r, _e, { orderNumber }) => [
        { type: "MarketplaceOrders", id: "LIST" },
        { type: "MarketplaceOrder", id: orderNumber },
      ],
    }),
  }),
});

export const {
  useListMarketplaceCategoriesQuery,
  useListMarketplaceProductsQuery,
  useGetMarketplaceProductQuery,
  useListMarketplaceOrdersQuery,
  useGetMarketplaceOrderQuery,
  useMarketplaceOrderFeePreviewMutation,
  useCreateMarketplaceOrderMutation,
  useCancelMarketplaceOrderMutation,
} = investorMarketplaceApi;
